import React from "react"
import { useMap } from "@vis.gl/react-google-maps"
import { LatLngObj, StrokeOptionsProps, ZOptionsProps } from "../lib/helpers/googleMap"
import { useGoogleMapsEffect } from "../lib/hooks/useGoogleMapsEffect"

interface PolylineProps {
  mapId: string
  path: LatLngObj[]
  strokeOptions?: StrokeOptionsProps
  zOptions?: ZOptionsProps
  lineStyle?: "solid" | "dashed"
  onClick?: (e: any) => void
  onMouseOver?: (e: any) => void
  onMouseOut?: (e: any) => void
  addBorder?: boolean
  borderColor?: string
}

const Polyline: React.FC<PolylineProps> = ({
  mapId,
  path,
  strokeOptions,
  zOptions,
  lineStyle,
  onClick,
  onMouseOver,
  onMouseOut,
  addBorder,
  borderColor,
}) => {
  const map = useMap(mapId)
  const polyLineRef = React.useRef<google.maps.Polyline | null>()
  const borderPolylineRef = React.useRef<google.maps.Polyline | null>()
  const polylineOptions = React.useMemo(() => {
    if (lineStyle === "dashed") {
      const lineSymbol = {
        path: "M 0,-1 0,1",
        scale: 4,
        ...(strokeOptions ?? {}),
        ...(zOptions ?? {}),
      }

      return {
        path,
        strokeOpacity: 0,
        ...(zOptions ?? {}),
        icons: [
          {
            icon: lineSymbol,
            offset: "0",
            repeat: "20px",
          },
        ],
      }
    }

    return {
      path,
      ...(strokeOptions ?? {}),
      ...(zOptions ?? {}),
    }
  }, [path, lineStyle, strokeOptions, zOptions])

  /**
   * Optionally create a second layer for the border,
   * with the same options
   * but different color and thicker weight
   */
  const borderPolylineOptions = React.useMemo(() => {
    if (!addBorder) return

    const borderStrokeOptions = {
      ...(strokeOptions ?? {}),
      ...(strokeOptions?.strokeWeight && { strokeWeight: strokeOptions?.strokeWeight + 1 }),
      ...(borderColor && { strokeColor: borderColor }),
      ...(zOptions ?? {}),
    }

    if (lineStyle === "dashed") {
      const lineSymbol = {
        path: "M 0,-1 0,1",
        scale: 4,
        ...(borderStrokeOptions ?? {}),
        ...(zOptions ?? {}),
      }

      return {
        path,
        strokeOpacity: 0,
        ...(zOptions ?? {}),
        icons: [
          {
            icon: lineSymbol,
            offset: "0",
            repeat: "20px",
          },
        ],
      }
    }

    return {
      path,
      ...(borderStrokeOptions ?? {}),
      ...(zOptions ?? {}),
    }
  }, [addBorder, path, lineStyle, strokeOptions, borderColor, zOptions])

  useGoogleMapsEffect(
    mapId,
    () => {
      // clear previous
      if (polyLineRef.current) {
        polyLineRef.current.setMap(null)
        polyLineRef.current = null
      }

      if (borderPolylineRef.current) {
        borderPolylineRef.current.setMap(null)
        borderPolylineRef.current = null
      }

      if (!path || path.length === 0) return

      // add border if needed (no need to add listeners)
      if (addBorder && borderPolylineOptions) {
        borderPolylineRef.current = new google.maps.Polyline({
          map,
          ...borderPolylineOptions,
        })
      }

      // add primary polyline
      const polyline = new google.maps.Polyline({
        map,
        ...polylineOptions,
      })

      // with listenters
      if (onClick && typeof onClick === "function") {
        polyline.addListener("click", onClick)
      }

      if (onMouseOver && typeof onMouseOver === "function") {
        polyline.addListener("mouseover", onMouseOver)
      }

      if (onMouseOut && typeof onMouseOut === "function") {
        polyline.addListener("mouseout", onMouseOut)
      }

      polyLineRef.current = polyline
    },
    [path, polyLineRef, polylineOptions, addBorder, borderPolylineOptions, borderPolylineRef],
  )

  // cleanup effect
  React.useEffect(() => {
    return () => {
      if (polyLineRef.current) {
        polyLineRef.current.setMap(null)
        polyLineRef.current = null
      }
      if (borderPolylineRef.current) {
        borderPolylineRef.current.setMap(null)
        borderPolylineRef.current = null
      }
    }
  }, [polyLineRef, borderPolylineRef])

  return null
}

export default Polyline
